import { pickT } from "@repo/shared";
import type { LocaleCode, PublicErrorReason, Translated } from "@repo/shared/types";
import { AlertCircle } from "lucide-react";
import { Suspense, useMemo } from "react";
import { useSearchParams } from "react-router";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/ui/link";
import { supportedLocaleOrNull } from "@/lib/api/public-flow/contact-info";
import { T } from "./page.i18n";

/**
 * `?reason=<PublicErrorReason>` maps to the same wording the auth pages use,
 * plus `eid_unavailable` (QL-570) when eID is off for the link's tenant. An unrecognized/absent reason is treated generically — falls
 * through to `?message=` (arbitrary text from e.g. a Criipto OAuth error) or
 * the default error text.
 */
function reasonMessage(t: Translated<typeof T>, reason: string | null): string | undefined {
  switch (reason as PublicErrorReason | null) {
    case "expired":
      return t.linkExpired;
    case "revoked":
      return t.linkRevoked;
    case "representative_removed":
      return t.representativeRemoved;
    case "representative_changed":
      return t.representativeChanged;
    case "member_removed":
      return t.memberRemoved;
    case "not_found":
      return t.linkNotFound;
    case "eid_unavailable":
      return t.eidUnavailable;
    default:
      return undefined;
  }
}

/**
 * QL-17 issue 027 — the page's language: `?lang=` (the API's eID redirects
 * add the flow's), then the one the public flow stored at submit, else
 * English. Only supported locales are taken.
 */
function errorPageLanguage(searchParams: URLSearchParams): LocaleCode {
  let stored: string | null = null;
  try {
    stored = sessionStorage.getItem("publicFlowLanguage");
  } catch {
    // Storage blocked (private mode): fall through to English.
  }
  return (
    supportedLocaleOrNull(searchParams.get("lang")) ?? supportedLocaleOrNull(stored) ?? "en_GB"
  );
}

function ErrorContent() {
  const [searchParams] = useSearchParams();
  const lang = useMemo(() => errorPageLanguage(searchParams), [searchParams]);

  const t = pickT(T, lang);
  const message =
    reasonMessage(t, searchParams.get("reason")) || searchParams.get("message") || t.defaultError;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
          <CardTitle className="text-2xl">{t.authFailed}</CardTitle>
          <CardDescription>{t.couldntVerify}</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{message}</AlertDescription>
          </Alert>

          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-4">{t.tryAgain}</p>
            <Button asChild variant="outline">
              <Link to="/">{t.goBack}</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function LoadingFallback() {
  const t = pickT(T, errorPageLanguage(new URLSearchParams(window.location.search)));
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
          <CardTitle className="text-2xl">{t.authFailed}</CardTitle>
          <CardDescription>{t.couldntVerify}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">{t.loading}</p>
        </CardContent>
      </Card>
    </div>
  );
}

export default function PublicAuthErrorPage() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <ErrorContent />
    </Suspense>
  );
}
